import type { LocationSuggestion } from '../services/neighborhoodMapService';
import { pruneHiddenFilters } from '../config/searchFilterFields';
import { dropOpenEndedMaxes, type RentPricePeriod } from '../config/searchPresets';

/**
 * The /search deep-link contract for the home-hero search card, as a pure
 * function so it is unit-testable without rendering anything. Moved here from
 * components/feature/AdvancedSearch.tsx, which re-exports it for old importers.
 */

/** A city chosen in the hero's city picker (a ResolvedSaudiCity fits this). */
export interface HeroSearchCity {
  /** Exact database City.id when known — filters by id, not by name. */
  cityId?: string | null;
  nameAr: string;
  nameEn: string;
}

/**
 * Everything the hero search card knows, flattened for URL building.
 */
export interface HeroSearchState {
  tab: 'buy' | 'rent' | 'sold';
  category: '' | 'residential' | 'commercial';
  /** The chip: an exact city/district picked from the location autocomplete. */
  location?: LocationSuggestion | null;
  /** The city picker's choice. A `location` (district or city) takes precedence. */
  city?: HeroSearchCity | null;
  /** Residual free text — optional keyword search within the location/filters. */
  keyword?: string;
  isRTL?: boolean;
  propertyType?: string;
  bedrooms?: string;
  bedroomsMax?: string;
  bathrooms?: string;
  bathroomsMax?: string;
  minArea?: string;
  maxArea?: string;
  minPrice?: string;
  maxPrice?: string;
  /** The period a rent price was typed in (شهري / سنوي). Sent with a rent price only. */
  pricePeriod?: RentPricePeriod;
  view?: 'map';
}

export function buildHeroSearchParams(input: HeroSearchState): URLSearchParams {
  // Never send a value the panel hides for this type / category (e.g. bedrooms
  // left over from a villa after switching to أرض).
  // A «إلى 5+» (the list's open-ended top) is no upper limit at all: drop it.
  const state = dropOpenEndedMaxes(pruneHiddenFilters(input));
  const params = new URLSearchParams();
  if (state.view) params.set('view', state.view);
  const keyword = state.keyword?.trim();
  if (keyword) params.set('q', keyword);
  // Location precedence: a district (exact area + centroid) beats a city
  // suggestion, which beats the city picker.
  const loc = state.location;
  if (loc) {
    if (loc.kind === 'city') {
      params.set('cityId', loc.id);
      // Carry the display name so the results page can show the city chip
      // (filtering is by the exact cityId, not this label).
      params.set('city', state.isRTL ? loc.nameAr || loc.nameEn : loc.nameEn);
    } else {
      params.set('areaId', loc.id);
      if (loc.latitude != null) params.set('locLat', String(loc.latitude));
      if (loc.longitude != null) params.set('locLng', String(loc.longitude));
    }
  } else if (state.city) {
    const { cityId, nameAr, nameEn } = state.city;
    if (cityId) {
      params.set('cityId', cityId);
      params.set('city', state.isRTL ? nameAr || nameEn : nameEn || nameAr);
    } else if (nameEn || nameAr) {
      // No database id known (cities endpoint unavailable): the backend's fuzzy
      // `city` match, by the English name it normalizes best.
      params.set('city', nameEn || nameAr);
    }
  }
  // Buy/Rent set the listing type. "Sold" is a disabled placeholder and can
  // never be the active tab, so it contributes no filter.
  if (state.tab === 'buy' || state.tab === 'rent') params.set('listingType', state.tab);
  // Macro-category, always explicit: '' (both) serializes as the 'all'
  // sentinel — without it the results page would re-default to residential.
  params.set('category', state.category || 'all');
  // Send the canonical UPPERCASE enum so the search page + backend match.
  if (state.propertyType) params.set('type', state.propertyType.toUpperCase());
  if (state.bedrooms) params.set('bedrooms', state.bedrooms);
  if (state.bedroomsMax) params.set('bedroomsMax', state.bedroomsMax);
  if (state.bathrooms) params.set('bathrooms', state.bathrooms);
  if (state.bathroomsMax) params.set('bathroomsMax', state.bathroomsMax);
  if (state.minArea) params.set('minArea', state.minArea);
  if (state.maxArea) params.set('maxArea', state.maxArea);
  if (state.minPrice) params.set('minPrice', state.minPrice);
  if (state.maxPrice) params.set('maxPrice', state.maxPrice);
  // Rent amounts carry their period, so the results compare like with like.
  if (state.tab === 'rent' && state.pricePeriod && (state.minPrice || state.maxPrice)) {
    params.set('pricePeriod', state.pricePeriod);
  }
  return params;
}
